import { useCallback, Fragment, Suspense, lazy } from "react";
import { GifType, IGif, IUser, INicheFeedbackResponse, TAddFeedback } from "@/common/services/types";
import { metricsBus, EventSource } from "@/common/buses";
import { onOpenMoreMenu } from "@/components/popup-dispatcher/PopupDispatcher";
import { useVisitorInfo } from "@/common/hooks/useVisitorInfo";
import { LikeSoundOverlay } from "../LikeSoundOverlay";
import { Player } from "../Player";
import { ImageGif } from "../ImageGif";
import { usePlayerSound } from "./hooks/usePlayerSound";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { Sidebar } from "../Sidebar";
import { MetaInfo } from "../MetaInfo";
import { useGalleryGif } from "./hooks/useGalleryGif";
import { useLikeActions } from "./hooks/useLikeActions";
import { useSfw } from "@/common/hooks/useSfw";
import { isLiteApp } from "@/common/utils/env";
import { FullScreenNav } from "../FullScreenNav";
import "./GifPreview.scss";
import { useAppStore } from "@/common/store/appStore";

const GalleryGif = lazy(() => import("@/components/gallery-gif/GalleryGif"));

interface IGifPreviewProps {
  user: IUser | undefined;
  rel?: string;
  position: number;
  wrapRef: React.RefObject<HTMLDivElement | null>;
  gif: IGif;
  isVisible: boolean;
  playCount: number;
  incrementPlayCount: () => void;
  hideGif: (id: string) => void;
  sendGifView: (gif: IGif) => void;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
}

export const GifPreview: React.FC<IGifPreviewProps> = (props) => {
  const {
    wrapRef,
    isVisible,
    position,
    hideGif,
    sendGifView,
    playCount,
    incrementPlayCount,
    nicheFeedbacks,
    addNicheFeedback,
    rel
  } = props;

  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const { sanitizeGif, isLoading } = useSfw();
  const { gif } = sanitizeGif(props.gif);

  const {
    currentGalleryGif,
    onSetGalleryGif
  } = useGalleryGif(gif);

  const currentUser = useVisitorInfo();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const { liked, handleLike } = useLikeActions({
    gifId: gif.id,
    source: eventSource?.name ?? EventSource.Other,
    sourceId: eventSource?.id ?? null,
    initialLikesCount: gif.likes,
    ref: wrapRef,
    boosted: !!gif.cta,
    position,
    playCount
  });

  const { sound, toggleSound } = usePlayerSound({ ref: wrapRef });

  const onMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, gif: IGif) => {
    onOpenMoreMenu({
      gif,
      anchorEl: event.currentTarget || null,
      position,
      playCount,
      hideGif,
      addNicheFeedback,
      nicheFeedbacks
    });
    metricsBus.emit("optionClicked");
  }, [hideGif, eventSource, position, playCount, addNicheFeedback, nicheFeedbacks]);

  if (isLoading) {
    return null;
  }

  return (
    <Fragment>
      {isFullScreen && <FullScreenNav gifId={gif.id} />}
      <div className="GifPreview-BackdropWrap">
        <img
          // eslint-disable-next-line react/no-unknown-property
          fetchPriority={position === 1 ? "high" : undefined}
          src={gif.urls.thumbnail}
          alt={`Poster for ${gif.id}`}
        />
      </div>

      {gif.type === GifType.Video &&
        (
          <Player
            poster={gif.urls.thumbnail}
            position={position}
            gif={gif}
            sound={sound}
            handleLike={handleLike}
            isVisible={isVisible}
            isHLS={!!gif.hls}
            onSound={toggleSound}
            currentUserName={currentUser?.username}
            sendGifView={sendGifView}
            playCount={playCount}
            incrementPlayCount={incrementPlayCount}
          />
        )
      }

      {gif.type === GifType.Image && (
        gif.gallery ? (
          <Suspense>
            <GalleryGif
              galleryId={gif.gallery}
              initialGif={gif}
              onSetGalleryGif={onSetGalleryGif}
              onLike={handleLike}
              isVisible={isVisible}
              sendGifView={sendGifView}
            />
          </Suspense>
        ) : (
          <ImageGif
            onLike={handleLike}
            gif={gif}
            isVisible={isVisible}
            sendGifView={sendGifView}
          />
        )
      )}

      <div className="GifPreview-InfoAndSidebar">
        <div className="GifPreview-MetaInfo" onContextMenu={event => event.preventDefault()}>
          <MetaInfo
            gif={currentGalleryGif !== null ? currentGalleryGif : gif}
            user={props.user}
            currentUser={currentUser}
            position={position}
            isVisible={isVisible}
            playCount={playCount}
            isLite={isLiteApp}
          />
        </div>
        <div className="GifPreview-SideBarWrap">
          <Sidebar
            onMenu={onMenu}
            onLike={handleLike}
            onSound={toggleSound}
            rel={rel}
            gifSound={sound}
            gif={currentGalleryGif !== null && !gif.aliasId ? currentGalleryGif : gif}
            liked={liked}
            likesCount={gif.likes}
            isLite={isLiteApp}
          />
        </div>
      </div>

      <LikeSoundOverlay gifId={gif.id} />
    </Fragment>
  );
};
